import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Injector,
  OnInit
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { HttpErrorResponse } from '@angular/common/http';
import { DialogService } from 'primeng/dynamicdialog';
import { DevicesClass, DeviceTypes } from '@app/core/classes';
import { AppStaticURL } from '@app/app.settings';
import { BookingService } from '@app/modules/booking-modules/booking.service';
import {
  UpdateVenueProfileDto,
  VenueDetailsVm
} from '@app/modules/booking-modules/booking.model';
import { FundamentalsService } from '@app/modules/fundamentals-modules/fundamentals.service';
import { CitiesModel, GenresModel } from '@app/modules/fundamentals-modules/fundamentals.models';
import { TokenService, RolesService } from '@app/core/services';
import { AccountService } from '@app/modules/account-modules/account.service';
import { getActiveBookingVenueCustomerId } from '@app/modules/account-modules/account.model';
import { SocialPlatform, SocialLinkDto, socialPlatformFromApiValue } from '@app/modules/account-modules/performers.model';
import {
  SOCIAL_PLATFORMS,
  PLATFORM_ICON_MAP,
  PlatformIconConfig
} from '@app/modules/performers-modules/client/performer-form.constants';
import { InputValidators } from '@app/core/validators/input.validator';
import { URLLinkValidate, PhoneNumberValidate } from '@app/utils/validator';
import { NotificationService } from '@app/modules/notification-modules/notification.service';
import { DialogIncludesTypes } from '@app/core/models/diallog';
import { BookingCustomerMainPhotoDialogComponent } from '@app/modules/booking-modules/client/dialogs/booking-customer-main-photo-dialog/booking-customer-main-photo-dialog.component';
import { BookingCustomerMainPhotoDialogData } from '@app/modules/booking-modules/client/dialogs/booking-customer-main-photo-dialog/booking-customer-main-photo-dialog.model';
import { VENUE_FACILITY_OPTION_KEYS } from '@app/shared/venue-customer-profile-fields/venue-customer-form.constants';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import { VenueGalleryEditDialogComponent } from '@app/modules/booking-modules/client/dialogs/venue-gallery-edit-dialog/venue-gallery-edit-dialog.component';
import { VenueGalleryEditDialogData } from '@app/modules/booking-modules/client/dialogs/venue-gallery-edit-dialog/venue-gallery-edit-dialog.model';
import { PhotoVm } from '@app/modules/account-modules/performers.model';

type VenueProfileTab = 'profile' | 'applications';

@UntilDestroy()
@Component({
  selector: 'app-profile-booking-customer',
  templateUrl: './profile-booking-customer.component.html',
  styleUrls: ['./profile-booking-customer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProfileBookingCustomerComponent extends DevicesClass implements OnInit {
  readonly DTypes = DeviceTypes;
  readonly StaticURL = AppStaticURL;
  readonly SocialPlatforms = SOCIAL_PLATFORMS;
  readonly platformIconMap = PLATFORM_ICON_MAP;

  activeTab: VenueProfileTab = 'profile';
  profile: VenueDetailsVm | null = null;
  loading = true;
  loadError: string | null = null;
  tokenError: string | null = null;
  bookingCustomerId: string | null = null;

  Cities: CitiesModel = [];
  Genres: GenresModel = [];
  editForm: FormGroup | null = null;
  editFormSubmitted = false;
  editSubmitLoading = false;
  editGlobalError: string | null = null;
  venueGalleryPreviewUrls: string[] = [];

  constructor(
    injector: Injector,
    public cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private bookingService: BookingService,
    private fundamentalsService: FundamentalsService,
    private token: TokenService,
    private roles: RolesService,
    private account: AccountService,
    private inputValidators: InputValidators,
    private dialogService: DialogService,
    private notificationService: NotificationService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.loadGenres();
    this.resolveBookingCustomerId();
  }

  setTab(tab: VenueProfileTab): void {
    this.activeTab = tab;
    this.cdr.markForCheck();
  }

  get socialLinksEditArray(): FormArray {
    return this.editForm!.get('socialLinks') as FormArray;
  }

  getPlatformIcon(platformValue: number): PlatformIconConfig {
    return this.platformIconMap[platformValue] ?? { type: 'svg', src: 'assets/images/icons/website.svg' };
  }

  platformToEnum(raw: unknown): SocialPlatform | null {
    return socialPlatformFromApiValue(raw);
  }

  displayName(): string {
    return (this.profile?.name || '').trim() || 'Заведение';
  }

  profileImageSrc(): string | null {
    const img = this.profile?.image;
    if (!img) {
      return null;
    }
    const rel = img.thumbnailPath || img.path;
    return rel ? this.StaticURL + rel.replace(/^\//, '') : null;
  }

  formatPhone(): string {
    const p = this.profile?.contacts;
    if (!p?.number) {
      return '';
    }
    const code = (p.code || '7').replace(/\D/g, '') || '7';
    return `+${code} ${p.number.replace(/\D/g, '')}`;
  }

  openGalleryEditDialog(): void {
    const id = this.bookingCustomerId;
    if (!id) {
      return;
    }
    const keys = (this.profile?.photos ?? []).map((p: PhotoVm) => p.key).filter(Boolean) as string[];
    const urls = (this.profile?.photos ?? [])
      .map((p: PhotoVm) => {
        const rel = p.thumbnailPath || p.path;
        return rel ? this.StaticURL + String(rel).replace(/^\//, '') : '';
      })
      .filter(Boolean);
    const ref = this.dialogService.open(VenueGalleryEditDialogComponent, {
      header: 'Фотоальбом',
      width: '50vw',
      data: { bookingCustomerId: id, currentKeys: keys, currentPhotoUrls: urls } as VenueGalleryEditDialogData
    });
    ref.onClose.pipe(untilDestroyed(this)).subscribe((result: { applied?: boolean } | undefined) => {
      if (result?.applied) {
        this.reloadProfileQuiet();
      }
      this.cdr.markForCheck();
    });
  }

  galleryPreviewUrls(): string[] {
    return this.venueGalleryPreviewUrls;
  }

  openMainPhotoEditDialog(): void {
    const id = this.bookingCustomerId;
    if (!id || !this.editForm) {
      return;
    }
    const ref = this.dialogService.open(BookingCustomerMainPhotoDialogComponent, {
      header: 'Фото профиля',
      width: '50vw',
      data: { preview: this.profileImageSrc(), bookingCustomerId: id } as BookingCustomerMainPhotoDialogData
    });
    ref.onClose.pipe(untilDestroyed(this)).subscribe((result: { applied?: boolean } | undefined) => {
      if (result?.applied) {
        this.reloadProfileQuiet();
      }
      this.cdr.markForCheck();
    });
  }

  addSocialLinkEdit(): void {
    this.socialLinksEditArray.push(
      this.fb.group({
        platform: [SocialPlatform.Instagram],
        url: ['', c => (c.value && !URLLinkValidate(c.value) ? { urlInvalid: true } : null)]
      })
    );
    this.editForm?.markAsDirty();
    this.cdr.markForCheck();
  }

  removeSocialLinkEdit(i: number): void {
    this.socialLinksEditArray.removeAt(i);
    this.editForm?.markAsDirty();
    this.cdr.markForCheck();
  }

  trackByIndex(index: number): number {
    return index;
  }

  saveProfile(): void {
    const id = this.bookingCustomerId;
    if (!id || !this.editForm || this.editSubmitLoading) {
      return;
    }
    this.editFormSubmitted = true;
    this.editGlobalError = null;

    const phone = String(this.editForm.get('phone')?.value || '');
    if (!PhoneNumberValidate(phone)) {
      this.editGlobalError = 'Укажите корректный телефон.';
      this.cdr.markForCheck();
      return;
    }

    if (this.getValidSocialLinksEdit().length < 1) {
      this.editGlobalError = 'Добавьте хотя бы одну корректную ссылку на соцсеть.';
      this.cdr.markForCheck();
      return;
    }

    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      this.cdr.markForCheck();
      return;
    }

    const v = this.editForm.getRawValue();
    const body: UpdateVenueProfileDto = {
      name: String(v.venueName || '').trim() || null,
      description: String(v.description || '').trim() || null,
      format: String(v.format || '').trim() || null,
      venueTypeTag: String(v.venueTypeTag || '').trim() || null,
      city: Number(v.city) || 0,
      preferredGenres: (v.preferredGenres || []).length ? v.preferredGenres : null,
      preferredPerformerTypes: (v.preferredPerformerTypes || []).length ? v.preferredPerformerTypes : null,
      socialLinks: this.getValidSocialLinksEdit(),
      facilities: this.buildFacilitiesPayload(v),
      contacts: {
        number: phone.replace(/\D/g, '').replace(/^7/, '').slice(-10),
        hasTelegram: !!v.hasTelegram,
        hasMax: !!v.hasMax
      }
    };

    const addressPayload = {
      address: {
        fullAddress: String(v.addressFullAddress || '').trim(),
        description: String(v.addressDescription || '').trim() || null
      }
    };

    this.editSubmitLoading = true;
    this.cdr.markForCheck();

    forkJoin({
      profile: this.bookingService.updateVenueBookingCustomerProfile(id, body),
      address: this.bookingService.updateVenueAddress(id, addressPayload),
      availability: this.bookingService.updateVenueAvailability(id, !!v.readyToAcceptApplications)
    })
      .pipe(untilDestroyed(this))
      .subscribe(
        () => {
          this.editSubmitLoading = false;
          this.editFormSubmitted = false;
          this.editForm!.markAsPristine();
          this.reloadProfileQuiet();
          this.notificationService.onDialog({
            type: DialogIncludesTypes.PopupSuccessTemplate,
            header: 'Сохранено',
            content: 'Профиль обновлён.'
          });
          this.cdr.markForCheck();
        },
        err => {
          this.editSubmitLoading = false;
          this.notificationService.onDialog({
            type: DialogIncludesTypes.PopupErrorTemplate,
            header: 'Ошибка',
            content: (err && (err.message || err.error?.message)) || 'Не удалось сохранить профиль.'
          });
          this.cdr.markForCheck();
        }
      );
  }

  private buildFacilitiesPayload(v: Record<string, unknown>): UpdateVenueProfileDto['facilities'] {
    const facilities: Record<string, unknown> = {};
    VENUE_FACILITY_OPTION_KEYS.forEach(key => {
      const level = (v[`facility_${key}_level`] || '').toString().trim();
      const notes = (v[`facility_${key}_notes`] || '').toString().trim();
      if (level || notes) {
        facilities[key] = { level: level || null, notes: notes || null };
      }
    });
    if (v.maxAudienceCapacity != null && v.maxAudienceCapacity !== '') {
      facilities.maxAudienceCapacity = Number(v.maxAudienceCapacity);
    }
    if (v.stageAreaSquareMeters != null && v.stageAreaSquareMeters !== '') {
      facilities.stageAreaSquareMeters = Number(v.stageAreaSquareMeters);
    }
    if (v.timeLimit) {
      facilities.timeLimit = String(v.timeLimit).trim() || null;
    }
    return Object.keys(facilities).length ? (facilities as UpdateVenueProfileDto['facilities']) : null;
  }

  private resolveBookingCustomerId(): void {
    const fromProfiles = getActiveBookingVenueCustomerId({ profiles: this.roles.accountProfiles });
    if (fromProfiles) {
      this.bookingCustomerId = fromProfiles;
      this.tokenError = null;
      this.loadCities();
      this.fetchProfile(fromProfiles);
      return;
    }
    if (!this.token.accessToken) {
      this.loading = false;
      this.tokenError = 'Не удалось определить профиль заведения. Войдите в аккаунт заново.';
      this.cdr.markForCheck();
      return;
    }
    this.account
      .getMyInfo()
      .pipe(take(1), untilDestroyed(this))
      .subscribe(
        user => {
          this.account.onSetUserRole(user);
          this.bookingCustomerId = getActiveBookingVenueCustomerId(user);
          if (!this.bookingCustomerId) {
            this.loading = false;
            this.tokenError = 'Не удалось определить профиль заведения. Войдите в аккаунт заново.';
            this.cdr.markForCheck();
            return;
          }
          this.tokenError = null;
          this.loadCities();
          this.fetchProfile(this.bookingCustomerId);
        },
        () => {
          this.loading = false;
          this.tokenError = 'Не удалось определить профиль заведения. Войдите в аккаунт заново.';
          this.cdr.markForCheck();
        }
      );
  }

  private getValidSocialLinksEdit(): SocialLinkDto[] {
    return (this.socialLinksEditArray.controls as FormGroup[])
      .map(ctrl => ({
        platform: ctrl.get('platform')!.value as SocialPlatform,
        url: (ctrl.get('url')!.value || '').trim()
      }))
      .filter(l => !!l.url && URLLinkValidate(l.url));
  }

  private loadCities(): void {
    try {
      const deps = this.fundamentalsService.citiesDependencies;
      this.Cities = deps?.length ? [{ name: 'Выберите город', id: 0 }, ...deps] : [{ name: 'Выберите город', id: 0 }];
    } catch {
      this.Cities = [{ name: 'Выберите город', id: 0 }];
    }
  }

  private loadGenres(): void {
    try {
      this.Genres = this.fundamentalsService.genresDependencies || [];
    } catch {
      this.Genres = [];
    }
  }

  private fetchProfile(id: string): void {
    this.loading = true;
    this.loadError = null;
    this.cdr.markForCheck();
    this.bookingService.getVenueBookingCustomerProfile(id).pipe(untilDestroyed(this)).subscribe(
      data => {
        this.loading = false;
        this.profile = data;
        this.syncGalleryPreviews(data);
        this.buildEditFormFromProfile(data);
        this.cdr.markForCheck();
      },
      (err: HttpErrorResponse) => {
        this.loading = false;
        this.loadError =
          (err.error && (err.error.message || err.error.Message)) || err.message || 'Не удалось загрузить профиль.';
        this.cdr.markForCheck();
      }
    );
  }

  private buildEditFormFromProfile(data: VenueDetailsVm): void {
    const phoneDigits = data.contacts?.number || '';
    const phoneDisplay = phoneDigits ? `7${phoneDigits.replace(/\D/g, '').slice(-10)}` : '';
    this.editForm = this.fb.group({
      venueName: [(data.name || '').trim(), Validators.required],
      format: [(data.format || '').trim()],
      description: [(data.description || '').trim()],
      venueTypeTag: [(data.venueTypeTag || '').trim()],
      city: [data.city && data.city > 0 ? data.city : 0, [Validators.required, this.inputValidators.checkIdValue]],
      preferredGenres: [data.preferredGenres || []],
      preferredPerformerTypes: [data.preferredPerformerTypes || []],
      readyToAcceptApplications: [!!data.readyToAcceptApplications],
      addressFullAddress: [(data.address?.fullAddress || '').trim()],
      addressDescription: [(data.address?.description || '').trim()],
      phone: [phoneDisplay],
      hasTelegram: [!!data.contacts?.hasTelegram],
      hasMax: [!!data.contacts?.hasMax],
      maxAudienceCapacity: [data.facilities?.maxAudienceCapacity ?? null],
      stageAreaSquareMeters: [data.facilities?.stageAreaSquareMeters ?? null],
      timeLimit: [data.facilities?.timeLimit || ''],
      socialLinks: this.fb.array([])
    });
    VENUE_FACILITY_OPTION_KEYS.forEach(key => {
      const opt = (data.facilities as Record<string, { level?: string; notes?: string }> | null)?.[key];
      this.editForm!.addControl(`facility_${key}_level`, this.fb.control(opt?.level || ''));
      this.editForm!.addControl(`facility_${key}_notes`, this.fb.control(opt?.notes || ''));
    });
    const arr = this.editForm.get('socialLinks') as FormArray;
    while (arr.length) {
      arr.removeAt(0);
    }
    for (const sl of data.socialLinks || []) {
      arr.push(
        this.fb.group({
          platform: [this.platformToEnum(sl.platform) ?? SocialPlatform.Instagram],
          url: [sl.url || '', c => (c.value && !URLLinkValidate(c.value) ? { urlInvalid: true } : null)]
        })
      );
    }
    if (!arr.length) {
      this.addSocialLinkEdit();
    }
    this.editForm.markAsPristine();
  }

  private reloadProfileQuiet(): void {
    const id = this.bookingCustomerId;
    if (!id) {
      return;
    }
    this.bookingService
      .getVenueBookingCustomerProfile(id)
      .pipe(untilDestroyed(this))
      .subscribe(
        data => {
          this.profile = data;
          this.syncGalleryPreviews(data);
          this.buildEditFormFromProfile(data);
          this.cdr.markForCheck();
        },
        () => {}
      );
  }

  private syncGalleryPreviews(data: VenueDetailsVm): void {
    this.venueGalleryPreviewUrls = (data.photos ?? [])
      .map((p: PhotoVm) => {
        const rel = p.thumbnailPath || p.path;
        return rel ? this.StaticURL + String(rel).replace(/^\//, '') : '';
      })
      .filter(Boolean);
  }
}
